import { Command } from 'cmdk';
import {
  Blocks,
  Box,
  Building2,
  ClipboardCheck,
  Compass,
  Database,
  FolderOpen,
  List,
  Loader2,
  Puzzle,
  Search,
  Shapes,
  type LucideIcon,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router';
import { api } from '../modules/platform/index.ts';
import type { NavItem } from '../modules/platform/index.ts';
import { projectPath } from '../modules/platform/index.ts';
import type { SearchGroup } from '../modules/platform/index.ts';
import { OPEN_SEARCH_EVENT } from '../modules/platform/index.ts';

/** Token ranking as the old palette's Pages group: every token must hit; prefixes rank higher. */
export function scorePage(value: string, search: string): number {
  const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (!tokens.length) return 1;
  const name = value.toLowerCase();
  const words = name.split(/[^a-z0-9]+/).filter(Boolean);
  let total = 0;
  for (const t of tokens) {
    const s = name.startsWith(t) ? 80 : words.some((w) => w.startsWith(t)) ? 70 : name.includes(t) ? 55 : 0;
    if (!s) return 0;
    total += s;
  }
  return total / (100 * tokens.length);
}

const GROUP_ICONS: Record<string, LucideIcon> = {
  element: Blocks,
  source: Box,
  target: Database,
  rule: Puzzle,
  reference_list: List,
  declaration: Shapes,
  requirement: ClipboardCheck,
  project: FolderOpen,
  org: Building2,
};

const MIN_CHARS = 2;
const DEBOUNCE_MS = 200;
const HEADING =
  '[&_[cmdk-group-heading]]:px-4 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-slate-400';
const ITEM =
  'w-full flex items-center gap-3 px-4 py-2 text-left cursor-pointer transition-colors data-[selected=true]:bg-indigo-50 dark:data-[selected=true]:bg-indigo-950/60';

/**
 * The Cmd+K palette, as the old SearchPalette: server-ranked groups from GET /api/search (design
 * rows of the projects the caller can open, projects, organizations) plus a client-side Pages group
 * over the nav. Hits in other projects open there; the rest open in the current project.
 */
export function CommandPalette({
  pages,
  slug,
  branch,
}: {
  pages: NavItem[];
  slug: string | null;
  branch: string | null;
}) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const [groups, setGroups] = React.useState<SearchGroup[]>([]);
  const [loading, setLoading] = React.useState(false);
  const seq = React.useRef(0);
  const navigate = useNavigate();

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    const onOpen = () => setOpen(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_SEARCH_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_SEARCH_EVENT, onOpen);
    };
  }, []);

  React.useEffect(() => {
    if (!open) {
      setQ('');
      setGroups([]);
    }
  }, [open]);

  // Debounced server search; a stale answer is dropped by its sequence number.
  React.useEffect(() => {
    if (!open) return;
    const query = q.trim();
    if (query.length < MIN_CHARS) {
      setGroups([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      const mine = ++seq.current;
      try {
        const { data } = await api.GET('/api/search', {
          params: { query: { q: query, ...(slug ? { project: slug } : {}), ...(branch ? { branch } : {}) } },
        });
        if (mine === seq.current) setGroups(data?.groups ?? []);
      } catch {
        if (mine === seq.current) setGroups([]);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, open, slug, branch]);

  const pageHits = React.useMemo(() => {
    if (!q.trim()) return pages;
    return pages
      .map((p) => ({ p, s: scorePage(`${p.title} ${p.page}`, q) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .slice(0, 5)
      .map((x) => x.p);
  }, [pages, q]);

  if (!open) return null;

  const go = (url: string) => {
    setOpen(false);
    navigate(url.startsWith('/') ? url : projectPath(url, slug, branch));
  };

  return (
    <div
      className="fixed inset-0 z-[100000] bg-black/40 flex justify-center items-start pt-[12vh] px-4"
      data-testid="search-palette"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <Command
        label="Search"
        loop
        shouldFilter={false}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-xl flex flex-col overflow-hidden border border-black/10 dark:border-white/10"
      >
        <div className="flex items-center gap-2 px-4 h-12 border-b border-black/10 dark:border-white/10 shrink-0">
          {loading ? (
            <Loader2 size={17} className="shrink-0 text-slate-400 animate-spin" />
          ) : (
            <Search size={17} className="shrink-0 text-slate-400" />
          )}
          <Command.Input
            autoFocus
            value={q}
            onValueChange={setQ}
            placeholder="Search everything — elements, sources, rules, apps, tasks, projects…"
            data-testid="search-palette-input"
            className="flex-1 h-full bg-transparent border-0 outline-none text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400"
          />
          <kbd className="hidden sm:inline-flex items-center rounded border border-black/10 dark:border-white/15 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
            esc
          </kbd>
        </div>
        <Command.List
          className="max-h-[52vh] overflow-y-auto overscroll-contain py-1"
          data-testid="search-palette-results"
        >
          {!loading && (
            <Command.Empty
              className="px-4 py-8 text-center text-sm text-slate-400"
              data-testid="search-palette-empty"
            >
              Nothing matches “{q.trim()}”.
            </Command.Empty>
          )}
          {groups.map((g) => {
            const Icon = GROUP_ICONS[g.kind] ?? Search;
            return (
              <Command.Group
                key={g.kind}
                heading={g.label}
                className={HEADING}
                data-testid={`search-group-${g.kind}`}
              >
                {g.items.map((item) => (
                  <Command.Item
                    key={`${g.kind}:${item.id}`}
                    value={`${g.kind}:${item.id}`}
                    onSelect={() => go(item.url)}
                    data-testid="search-palette-result"
                    className={ITEM}
                  >
                    <span className="shrink-0 text-base leading-none w-5 text-center">
                      <Icon size={16} className="text-slate-400 inline-block" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                        {item.name}
                      </span>
                      {(item.description ||
                        (item.project && item.project !== slug && g.kind !== 'project')) && (
                        <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
                          {item.project && item.project !== slug && g.kind !== 'project'
                            ? `${item.project} · `
                            : ''}
                          {item.description}
                        </span>
                      )}
                    </span>
                    {item.status ? (
                      <span className="shrink-0 text-[10px] uppercase tracking-wide text-slate-400">
                        {item.status}
                      </span>
                    ) : null}
                  </Command.Item>
                ))}
              </Command.Group>
            );
          })}
          {pageHits.length > 0 && (
            <Command.Group heading="Pages" className={HEADING}>
              {pageHits.map((p) => {
                const Icon = p.icon ?? Compass;
                return (
                  <Command.Item
                    key={p.page}
                    value={`page:${p.page}`}
                    onSelect={() => go(p.page)}
                    data-testid="search-palette-result"
                    className={ITEM}
                  >
                    <span className="shrink-0 text-base leading-none w-5 text-center">
                      <Icon size={16} className="text-slate-400 inline-block" />
                    </span>
                    <span className="min-w-0 flex-1 block text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {p.title}
                    </span>
                  </Command.Item>
                );
              })}
            </Command.Group>
          )}
        </Command.List>
        <div className="flex items-center gap-3 px-4 h-8 border-t border-black/10 dark:border-white/10 text-[11px] text-slate-400 shrink-0">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span className="ml-auto">smart search across the whole workspace</span>
        </div>
      </Command>
    </div>
  );
}
